import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Trophy, Plane, Users, Target, TrendingUp, Medal, Award,
  Search, Filter, ChevronDown, Star, Crown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContestsPage = () => {
  const { user } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState(null);
  const [contestData, setContestData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDM, setFilterDM] = useState('');
  const [sortBy, setSortBy] = useState('srNo');

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`${API}/contests/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContests(res.data.contests || []);
      if (res.data.contests?.length > 0) {
        loadContestData(res.data.contests[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      toast.error('Failed to load contests');
      setLoading(false);
    }
  };

  const loadContestData = async (contestId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`${API}/contests/${contestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedContest(res.data.contest);
      setContestData(res.data);
    } catch (err) {
      toast.error('Failed to load contest data');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-emerald-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 50) return 'bg-amber-500';
    if (percent >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (percent) => {
    if (percent >= 100) return { text: 'Qualified', color: 'bg-emerald-100 text-emerald-700' };
    if (percent >= 75) return { text: 'On Track', color: 'bg-blue-100 text-blue-700' };
    if (percent >= 50) return { text: 'In Progress', color: 'bg-amber-100 text-amber-700' };
    return { text: 'Needs Focus', color: 'bg-red-100 text-red-700' };
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return null;
  };

  // Get unique DM names for filter
  const dmNames = contestData?.achievers 
    ? [...new Set(contestData.achievers.map(a => a.dmName))].sort()
    : [];

  // Filter and sort achievers
  const filteredAchievers = contestData?.achievers
    ?.filter(a => {
      const matchesSearch = a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           a.dmName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDM = !filterDM || a.dmName === filterDM;
      return matchesSearch && matchesDM;
    })
    .sort((a, b) => {
      if (sortBy === 'srNo') return a.srNo - b.srNo;
      if (sortBy === 'achieved') return b.achievedPercent - a.achievedPercent;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'dm') return a.dmName.localeCompare(b.dmName);
      return 0;
    }) || [];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  if (contests.length === 0) {
    return (
      <div className="p-6 lg:p-8">
        <div className="text-center py-16">
          <Trophy className="w-20 h-20 text-stone-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-700">No Contests Yet</h2>
          <p className="text-stone-500 mt-2">Contests will appear here when created</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gradient-to-br from-stone-50 to-stone-100 min-h-screen" data-testid="contests-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl">
                <Trophy className="w-7 h-7 text-white" />
              </div>
              DBK Group Contests
            </h1>
            <p className="text-stone-500 mt-1">Track incentive programs and achiever progress</p>
          </div>
        </div>
      </div>

      {/* Contest Selection */}
      {contests.length > 1 && (
        <div className="mb-6">
          <div className="flex gap-2 flex-wrap">
            {contests.map(contest => (
              <Button
                key={contest.id}
                variant={selectedContest?.id === contest.id ? 'default' : 'outline'}
                onClick={() => loadContestData(contest.id)}
                className={selectedContest?.id === contest.id ? 'bg-emerald-600' : ''}
              >
                {contest.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Contest Banner */}
      {selectedContest && (
        <Card className="mb-6 overflow-hidden border-0 shadow-xl">
          <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <Plane className="w-10 h-10" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedContest.name}</h2>
                  <p className="text-emerald-100 mt-1">{selectedContest.description}</p>
                  {selectedContest.destination && (
                    <p className="text-amber-300 font-semibold mt-2 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Destination: {selectedContest.destination}
                    </p>
                  )}
                  {selectedContest.endDate && (
                    <p className="text-white/90 mt-1 text-sm">
                      Last Date: {new Date(selectedContest.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                  selectedContest.status === 'active' ? 'bg-green-400 text-green-900' :
                  selectedContest.status === 'completed' ? 'bg-gray-400 text-gray-900' :
                  'bg-amber-400 text-amber-900'
                }`}>
                  {selectedContest.status?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      {contestData?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4 text-center">
              <Award className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{contestData.stats.completed}</p>
              <p className="text-sm text-emerald-100">Qualified (100%)</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{contestData.stats.above50}</p>
              <p className="text-sm text-blue-100">Above 50%</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{contestData.stats.below50}</p>
              <p className="text-sm text-amber-100">Below 50%</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{contestData.stats.total}</p>
              <p className="text-sm text-purple-100">Total Participants</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-500 to-teal-600 text-white">
            <CardContent className="p-4 text-center">
              <Star className="w-8 h-8 mx-auto mb-2 opacity-80" />
              <p className="text-3xl font-bold">{contestData.stats.avgAchieved}%</p>
              <p className="text-sm text-teal-100">Avg. Achievement</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6 border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <input
                type="text"
                placeholder="Search by name or DM..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <select
                value={filterDM}
                onChange={(e) => setFilterDM(e.target.value)}
                className="px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 w-full md:w-48"
                data-testid="filter-dm"
              >
                <option value="">All DMs</option>
                {dmNames.map(dm => (
                  <option key={dm} value={dm}>{dm}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 w-full md:w-40"
                data-testid="sort-by"
              >
                <option value="srNo">SR No.</option>
                <option value="achieved">Achievement %</option>
                <option value="name">Name</option>
                <option value="dm">DM Name</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Achievers Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-stone-800 to-stone-900 text-white">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            Bangkok Achievers Data
            <span className="ml-2 px-2 py-0.5 bg-amber-500 text-amber-900 text-xs rounded-full font-bold">
              {filteredAchievers.length} participants
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="achievers-table">
              <thead className="bg-stone-100 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">SR No.</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Particulars</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">DM Name</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-stone-600 uppercase tracking-wider">Achieved %</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-stone-600 uppercase tracking-wider">Balance %</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-stone-600 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredAchievers.map((achiever, index) => {
                  const status = getStatusBadge(achiever.achievedPercent);
                  const isTopPerformer = achiever.achievedPercent >= 100;
                  
                  return (
                    <tr 
                      key={achiever.id || index}
                      className={`hover:bg-stone-50 transition-colors ${isTopPerformer ? 'bg-emerald-50/50' : ''}`}
                      data-testid={`achiever-row-${achiever.srNo}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getRankIcon(index + 1)}
                          <span className={`font-mono font-bold ${isTopPerformer ? 'text-emerald-700' : 'text-stone-700'}`}>
                            {achiever.srNo}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            isTopPerformer ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-stone-400 to-stone-500'
                          }`}>
                            {achiever.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <p className={`font-semibold ${isTopPerformer ? 'text-emerald-700' : 'text-stone-800'}`}>
                              {achiever.name}
                              {isTopPerformer && <Star className="w-4 h-4 inline ml-1 text-amber-500" />}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-stone-600 font-medium">{achiever.dmName}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`font-bold text-lg ${
                            achiever.achievedPercent >= 100 ? 'text-emerald-600' :
                            achiever.achievedPercent >= 50 ? 'text-blue-600' : 'text-stone-600'
                          }`}>
                            {achiever.achievedPercent}%
                          </span>
                          <div className="w-24 h-2 bg-stone-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getProgressColor(achiever.achievedPercent)} transition-all duration-500`}
                              style={{ width: `${Math.min(achiever.achievedPercent, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${
                          achiever.balancePercent === 0 ? 'text-emerald-600' : 'text-red-500'
                        }`}>
                          {achiever.balancePercent}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.color}`}>
                          {status.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {filteredAchievers.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500">No achievers found matching your criteria</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DM Leaderboard */}
      {contestData?.dmStats && Object.keys(contestData.dmStats).length > 0 && (
        <Card className="mt-6 border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              DM Performance Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(contestData.dmStats)
                .sort((a, b) => b[1].avgAchieved - a[1].avgAchieved)
                .map(([dm, stats], index) => (
                  <div 
                    key={dm}
                    className={`p-4 rounded-xl border-2 ${
                      index === 0 ? 'border-amber-400 bg-amber-50' :
                      index === 1 ? 'border-gray-300 bg-gray-50' :
                      index === 2 ? 'border-amber-600 bg-amber-50/50' :
                      'border-stone-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {index === 0 && <Crown className="w-5 h-5 text-amber-500" />}
                        {index === 1 && <Medal className="w-5 h-5 text-gray-400" />}
                        {index === 2 && <Medal className="w-5 h-5 text-amber-600" />}
                        <span className="font-bold text-stone-800">{dm}</span>
                      </div>
                      <span className="text-xs bg-stone-200 px-2 py-1 rounded-full">
                        {stats.count} members
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(stats.avgAchieved)}`}
                          style={{ width: `${Math.min(stats.avgAchieved, 100)}%` }}
                        />
                      </div>
                      <span className="font-bold text-emerald-600">{stats.avgAchieved.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContestsPage;
